/**
 * NR buttons on the blocks that end where their counter goes unmanned, pinned to the block's bottom-right
 * corner (the phone icon keeps the top-left). Red while the officer is still to be told; white once told,
 * still tappable to undo; green "R" when relief has come after all and the officer is owed that call.
 * Synced with the No Relief pop-up through NoReliefContext. Sized from the block, so it scales with the grid
 * on every screen; a block too narrow for the label beside its name gets the same pill without text.
 */
import { useState } from 'react';
import { useNoReliefView } from '@/contexts/NoReliefContext';
import { formatCounterLabel } from '@/config/modeConfig';
import type { NoReliefMarkState } from '@/utils/noRelief';
import { blockCellW, nrButtonFit, placeMarks, type BlockRect, type CallGridView } from '@/utils/callIcons';

const LOOK: Record<NoReliefMarkState, { label: string; fill: string; ink: string; ring: string }> = {
  owed: { label: 'NR', fill: '#dc2626', ink: '#fff', ring: '#b91c1c' },
  told: { label: 'NR', fill: '#fff', ink: '#94a3b8', ring: '#cbd5e1' },
  relief: { label: 'R', fill: '#16a34a', ink: '#fff', ring: '#15803d' },
};

const TITLES: Record<NoReliefMarkState, (counter: string) => string> = {
  owed: (c) => `${c} has no relief — tap once you've told the officer`,
  told: (c) => `${c}: officer told there is no relief — tap to undo`,
  relief: (c) => `${c} has relief now — tap once you've told the officer`,
};

export function NoReliefCellOverlay({ rects, view, operationMode }: {
  rects: BlockRect[];
  view: CallGridView;
  operationMode?: string;
}) {
  const { marks, canEdit, toggle } = useNoReliefView();
  const [busy, setBusy] = useState<string | null>(null);
  return (
    <>
      {placeMarks(marks, rects, view).map(({ mark, rect }) => {
        const look = LOOK[mark.state];
        const fit = nrButtonFit(rect, look.label.length, blockCellW(rect));
        if (!fit) return null;
        const { w, h, compact, stacked, font } = fit;
        const key = `${mark.entry.counter}:${mark.entry.slot}|${rect.officerId}`;
        const counter = formatCounterLabel(mark.entry.counter + 1, operationMode);
        return (
          <button
            key={key}
            type="button"
            data-testid="nr-button"
            data-state={mark.state}
            disabled={!canEdit || busy === key}
            title={canEdit ? TITLES[mark.state](counter) : `View only — ${TITLES[mark.state](counter).split(' — ')[0]}`}
            onClick={(e) => {
              e.stopPropagation();
              setBusy(key);
              void toggle(mark).finally(() => setBusy(null));
            }}
            style={{
              // Below the sticky label column and time header, like the phone icons, so scrolling hides it with its cell.
              position: 'absolute', zIndex: 1, left: rect.x + rect.w - w - 1, top: rect.y + rect.h - h - 1,
              width: w, height: h, padding: 0, boxSizing: 'border-box', borderRadius: Math.round(h / 3),
              // A block too narrow for the label keeps its name readable: the same coloured pill, without the text.
              background: look.fill, color: look.ink,
              border: `1px solid ${look.ring}`,
              fontSize: stacked ? font : 9, fontWeight: 800, lineHeight: 1, letterSpacing: '0.02em',
              boxShadow: mark.state === 'told' ? undefined : '0 1px 2px rgba(0,0,0,0.3)',
              cursor: canEdit ? 'pointer' : 'default', opacity: busy === key ? 0.6 : 1,
              display: 'inline-flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {compact ? (stacked ? look.label.split('').map((c, i) => <span key={i}>{c}</span>) : null) : look.label}
          </button>
        );
      })}
    </>
  );
}
